import * as THREE from "three";
import { LevelSim } from "@highmarch/domain";
import { AnimRig } from "../rig";
import { HQ_YAW, lib } from "./common";
import { groundAt, roadLevelAt } from "../terrain";

/**
 * Tier-IV creatures on the map (M21, docs/TOWERS_V2.md "Tier 4"), drawn from the sim's records each frame:
 *  - the Phoenix Roost's phoenix (`sim.phoenixes`): the shipped flyer's model in a coat of fire — gold-orange, glowing —
 *    on its orbit (eased between ticks), banking into the turn; it fades out when it burns out and flares in when it rises;
 *  - the Venom Nest's spiders (`sim.spiders`): small dark-green spiders with a venom-green back, scuttling between the nest
 *    and the raider they ride, legs ticking;
 *  - Polymorph's sheep (`enemy.sheepTicksLeft`): a woolly white sheep standing in for the raider's body, which hides.
 * Simple models, built once from primitives (no art needed) except the phoenix, which falls back to a fire-coloured
 * bird of primitives without the flyer model.
 */

const PHOENIX_HEIGHT = 1.15;
const PHOENIX_SCALE = 0.72;
const FIRE = 0xff8a2a, FIRE_GLOW = 0xffb347;
const SPIDER_SCALE = 0.13;
const SPIDER_EASE = 12;
const SHEEP_SCALE = 0.3;

function phoenixMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color: FIRE, emissive: FIRE_GLOW, emissiveIntensity: 1.6, roughness: 0.5, metalness: 0.1 });
}

/** The phoenix's body: the flyer model recoloured in fire, else a primitive bird. */
function buildPhoenix(): { root: THREE.Group; rig: AnimRig | null } {
  const root = new THREE.Group();
  const L = lib();
  const mat = phoenixMaterial();
  if (L?.hq("enemy_flyer")) {
    const body = L.clone("hq:enemy_flyer");
    body.rotation.y = HQ_YAW;
    body.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.material = mat; });
    body.scale.multiplyScalar(PHOENIX_SCALE);
    root.add(body);
    const rig = new AnimRig(body, L.clipsOf("hq:enemy_flyer"));
    rig.playLoop("walk", 1.4, true);
    return { root, rig };
  }
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), mat);
  body.scale.set(1, 0.8, 1.6);
  const wingGeo = new THREE.BoxGeometry(0.5, 0.02, 0.18);
  for (const s of [-1, 1]) { const w = new THREE.Mesh(wingGeo, mat); w.position.set(0.25 * s, 0.02, 0); w.name = s < 0 ? "wingL" : "wingR"; root.add(w); }
  root.add(body);
  return { root, rig: null };
}

/** A spider of primitives: a body, a venom-green back, eight legs (two groups that tick in turn). */
function buildSpider(): THREE.Group {
  const g = new THREE.Group();
  const dark = new THREE.MeshStandardMaterial({ color: 0x1d2a18, roughness: 0.6 });
  const venom = new THREE.MeshStandardMaterial({ color: 0x7dff5a, emissive: 0x3dbb22, emissiveIntensity: 0.9 });
  const abdomen = new THREE.Mesh(new THREE.SphereGeometry(0.55, 10, 8), dark);
  abdomen.scale.set(1, 0.75, 1.2); abdomen.position.set(0, 0.55, -0.45);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.32, 8, 6), dark);
  head.position.set(0, 0.45, 0.35);
  const mark = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), venom);
  mark.scale.set(1, 0.4, 1.4); mark.position.set(0, 0.95, -0.45);
  g.add(abdomen, head, mark);
  const legGeo = new THREE.BoxGeometry(0.9, 0.07, 0.07);
  for (let i = 0; i < 8; i++) {
    const side = i < 4 ? -1 : 1, k = i % 4;
    const leg = new THREE.Mesh(legGeo, dark);
    leg.position.set(side * 0.55, 0.35, 0.25 - k * 0.25);
    leg.rotation.set(0, side * (k - 1.5) * 0.35, side * -0.5);
    leg.name = `leg${(i + k) % 2}`;
    g.add(leg);
  }
  g.scale.setScalar(SPIDER_SCALE);
  return g;
}

/** A sheep of primitives: woolly puffs, a dark face and legs. */
function buildSheep(): THREE.Group {
  const g = new THREE.Group();
  const wool = new THREE.MeshStandardMaterial({ color: 0xf6f1e4, roughness: 0.95 });
  const face = new THREE.MeshStandardMaterial({ color: 0x2b2420, roughness: 0.7 });
  const puff = new THREE.SphereGeometry(0.4, 10, 8);
  for (const [x, y, z] of [[0, 0.75, 0], [0.28, 0.82, 0.18], [-0.28, 0.82, 0.18], [0.25, 0.8, -0.25], [-0.25, 0.8, -0.25], [0, 1.0, 0]] as const) {
    const p = new THREE.Mesh(puff, wool); p.position.set(x, y, z); p.scale.setScalar(0.85 + 0.15 * Math.abs(x)); g.add(p);
  }
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.22, 8, 6), face);
  head.scale.set(0.9, 1, 1.3); head.position.set(0, 0.85, 0.6);
  g.add(head);
  const legGeo = new THREE.BoxGeometry(0.1, 0.45, 0.1);
  for (const [x, z] of [[0.2, 0.25], [-0.2, 0.25], [0.2, -0.25], [-0.2, -0.25]] as const) { const l = new THREE.Mesh(legGeo, face); l.position.set(x, 0.22, z); g.add(l); }
  g.scale.setScalar(SHEEP_SCALE);
  return g;
}

interface PhoenixView { root: THREE.Group; rig: AnimRig | null; prevX: number; prevZ: number; curX: number; curZ: number; shown: number; alive: boolean; yaw: number }
interface SpiderView { root: THREE.Group; x: number; z: number; tx: number; tz: number; riding: boolean; phase: number }

export class Tier4Creatures {
  readonly group = new THREE.Group();
  private readonly phoenixes = new Map<number, PhoenixView>();
  private readonly spiders = new Map<number, SpiderView>();
  private readonly sheep = new Map<number, THREE.Group>();
  /** Set by the owner: the enemy views' feet and a way to hide a polymorphed raider's body. */
  enemyFeet: (id: number) => THREE.Vector3 | null = () => null;
  hideBody: (id: number, hidden: boolean) => void = () => {};
  private time = 0;

  constructor(private readonly sim: LevelSim) {}

  /** After each sim tick: the creatures' targets. */
  onTick(): void {
    const seen = new Set<number>();
    for (const p of this.sim.phoenixes) {
      seen.add(p.towerId);
      let v = this.phoenixes.get(p.towerId);
      if (!v) { const b = buildPhoenix(); v = { ...b, prevX: p.x.toNumber(), prevZ: p.y.toNumber(), curX: p.x.toNumber(), curZ: p.y.toNumber(), shown: 0, alive: p.alive, yaw: 0 }; this.phoenixes.set(p.towerId, v); this.group.add(v.root); }
      v.prevX = v.curX; v.prevZ = v.curZ; v.curX = p.x.toNumber(); v.curZ = p.y.toNumber(); v.alive = p.alive;
    }
    for (const [id, v] of this.phoenixes) if (!seen.has(id)) { this.group.remove(v.root); this.phoenixes.delete(id); }
    const sp = new Set<number>();
    for (const s of this.sim.spiders) {
      sp.add(s.id);
      let v = this.spiders.get(s.id);
      if (!v) { const t = this.sim.findTower(s.towerId); const x = t ? t.cell.x : s.x.toNumber(), z = t ? t.cell.y : s.y.toNumber(); v = { root: buildSpider(), x, z, tx: x, tz: z, riding: false, phase: Math.random() * 6 }; this.spiders.set(s.id, v); this.group.add(v.root); }
      v.tx = s.x.toNumber(); v.tz = s.y.toNumber(); v.riding = s.enemyId !== 0;
    }
    for (const [id, v] of this.spiders) if (!sp.has(id)) { this.group.remove(v.root); this.spiders.delete(id); }
    const sh = new Set<number>();
    for (const e of this.sim.enemySlots) {
      if (!e.alive || e.sheepTicksLeft <= 0) continue;
      sh.add(e.id);
      if (!this.sheep.has(e.id)) { const g = buildSheep(); this.sheep.set(e.id, g); this.group.add(g); this.hideBody(e.id, true); }
    }
    for (const [id, g] of this.sheep) if (!sh.has(id)) { this.group.remove(g); this.sheep.delete(id); this.hideBody(id, false); }
  }

  /** Per frame: `alpha` the tick fraction. */
  update(alpha: number, dt: number): void {
    this.time += dt;
    for (const v of this.phoenixes.values()) {
      const x = v.prevX + (v.curX - v.prevX) * alpha, z = v.prevZ + (v.curZ - v.prevZ) * alpha;
      const dx = v.curX - v.prevX, dz = v.curZ - v.prevZ;
      if (dx * dx + dz * dz > 1e-6) v.yaw = Math.atan2(dx, dz);
      v.shown = v.alive ? Math.min(1, v.shown + dt * 3) : Math.max(0, v.shown - dt * 2.5);
      v.root.visible = v.shown > 0.01;
      v.root.position.set(x, groundAt(x, z) + PHOENIX_HEIGHT + Math.sin(this.time * 3) * 0.06, z);
      v.root.rotation.set(0, v.yaw, -0.35);
      v.root.scale.setScalar(v.shown);
      v.rig?.update(dt);
      if (!v.rig) for (const w of v.root.children) if (w.name.startsWith("wing")) w.rotation.z = Math.sin(this.time * 12) * 0.5 * (w.name === "wingL" ? 1 : -1);
    }
    for (const v of this.spiders.values()) {
      const k = 1 - Math.exp(-SPIDER_EASE * dt);
      const ox = v.x, oz = v.z;
      v.x += (v.tx - v.x) * k; v.z += (v.tz - v.z) * k;
      const moving = Math.hypot(v.x - ox, v.z - oz) > 1e-4;
      if (moving) v.root.rotation.y = Math.atan2(v.x - ox, v.z - oz);
      v.phase += dt * (moving ? 22 : 4);
      const lift = v.riding ? 0.32 : 0;
      v.root.position.set(v.x, roadLevelAt(v.x, v.z) + lift + Math.abs(Math.sin(v.phase)) * 0.015, v.z);
      for (const l of v.root.children) if (l.name.startsWith("leg")) l.rotation.x = Math.sin(v.phase + (l.name === "leg0" ? 0 : Math.PI)) * 0.4;
    }
    for (const [id, g] of this.sheep) {
      const f = this.enemyFeet(id);
      if (!f) continue;
      g.position.set(f.x, f.y, f.z);
      g.rotation.y += dt * 0.6;
      g.position.y += Math.abs(Math.sin(this.time * 9 + id)) * 0.04;
    }
  }

  dispose(): void {
    for (const id of this.sheep.keys()) this.hideBody(id, false);
    this.group.clear(); this.phoenixes.clear(); this.spiders.clear(); this.sheep.clear();
  }
}
